import { useEffect } from 'react';
import { SITE_URL } from '@/config/site';

interface SEOOptions {
  title: string;
  description: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  type?: 'website' | 'article' | 'profile';
  url?: string;
}

/** Truncate title to fit SERP display (max 60 chars) */
function truncateTitle(title: string, max = 60): string {
  if (title.length <= max) return title;
  // Try to cut at the pipe separator to keep brand
  const pipeIndex = title.lastIndexOf(' | ');
  if (pipeIndex > 0 && pipeIndex <= max) {
    const brand = title.slice(pipeIndex);
    const available = max - brand.length;
    if (available > 10) {
      return title.slice(0, available - 1).trimEnd() + '…' + brand;
    }
  }
  return title.slice(0, max - 1).trimEnd() + '…';
}

/** Truncate description to fit SERP display (max 155 chars) */
function truncateDescription(desc: string, max = 155): string {
  if (desc.length <= max) return desc;
  // Try to cut at last sentence boundary
  const truncated = desc.slice(0, max - 1);
  const lastPeriod = truncated.lastIndexOf('. ');
  if (lastPeriod > max * 0.5) {
    return truncated.slice(0, lastPeriod + 1);
  }
  // Cut at last word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > max * 0.6) {
    return truncated.slice(0, lastSpace) + '…';
  }
  return truncated + '…';
}

export const useSEO = ({ 
  title: rawTitle, 
  description: rawDescription, 
  image, 
  imageWidth = 1200, 
  imageHeight = 630,
  type = 'website', 
  url 
}: SEOOptions) => {
  const title = truncateTitle(rawTitle);
  const description = truncateDescription(rawDescription);

  useEffect(() => {
    document.title = title;

    const canonicalUrl = url || `${SITE_URL}${window.location.pathname}`;
    const defaultImage = `${SITE_URL}/lovable-uploads/ece2f963-be6a-4c06-be17-e49ee5eb60b2.png`;
    const ogImage = image || defaultImage;
    
    const updateOrCreateMeta = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) meta.setAttribute('property', name);
        else meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateOrCreateMeta('description', description);
    updateOrCreateMeta('og:title', title, true);
    updateOrCreateMeta('og:description', description, true);
    updateOrCreateMeta('og:type', type, true);
    updateOrCreateMeta('og:url', canonicalUrl, true);
    updateOrCreateMeta('og:site_name', 'BRRGR', true);
    updateOrCreateMeta('og:image', ogImage, true);
    updateOrCreateMeta('og:image:secure_url', ogImage, true);
    updateOrCreateMeta('og:image:width', String(imageWidth), true);
    updateOrCreateMeta('og:image:height', String(imageHeight), true);
    updateOrCreateMeta('og:image:alt', title, true);
    updateOrCreateMeta('og:image:type', 'image/png', true);
    updateOrCreateMeta('robots', 'index, follow');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

  }, [title, description, image, imageWidth, imageHeight, type, url]);
};
